import React, { useCallback, useLayoutEffect, useRef, useState } from 'react';
import { CORAL } from '../theme/creamyTokens';

interface SwipeCardCarouselProps {
  /** One slide per child -- each takes the carousel's full width. */
  children: React.ReactNode[];
}

/**
 * A one-card-at-a-time horizontal carousel, built on native CSS scroll
 * snapping rather than a drag/gesture library.
 *
 * That choice is deliberate: this renders over the /explore map, whose own
 * SDK handles touch, and next to the results sheet, which drags vertically --
 * a JS gesture handler here would have to win against both. Letting the
 * browser own the gesture sidesteps that entirely and brings momentum,
 * rubber-banding, trackpad and keyboard scrolling along for free.
 *
 * The padding all round (rather than a bare scroller) is what keeps each
 * card's own drop shadow from being clipped: a scroll container clips
 * anything painted outside its content box, shadows included, and
 * `overflow-x: auto` forces the used value of `overflow-y` to `auto` too, so
 * there's no opting out on either axis. The negative horizontal margin hands
 * that side's padding back, so a card stays exactly as wide as it was before
 * there was a carousel around it; callers offset their own bottom inset by
 * CAROUSEL_PADDING to leave a single card sitting exactly where it did. That
 * same padding is what leaves the neighbouring cards peeking in at either
 * edge, which is what makes the scaling below visible at rest and not only
 * mid-swipe.
 */
const CAROUSEL_PADDING = 16;

// The card in focus keeps its full size and a neighbour bottoms out here, one
// slide's distance away -- everything in between is interpolated from the live
// scroll position, so a card grows as it comes to the middle and shrinks as it
// leaves rather than snapping between two sizes.
const FOCUSED_SCALE = 1;
const NEIGHBOUR_SCALE = 0.88;

const SwipeCardCarousel = ({ children }: SwipeCardCarouselProps) => {
  const scrollerRef = useRef<HTMLDivElement>(null);
  const [index, setIndex] = useState(0);

  const syncToScroll = useCallback(() => {
    const scroller = scrollerRef.current;
    if (!scroller) return;
    const slides = Array.from(scroller.children) as HTMLElement[];
    // Measured off a real slide rather than derived from the scroller's own
    // width, which counts the padding above -- one less thing to keep in step
    // if that padding ever changes.
    const slideWidth = slides[0]?.clientWidth;
    if (!slideWidth) return;

    // Every read first, every write after: interleaving them would force the
    // browser to re-run layout once per card, on every scroll frame.
    // offsetLeft is in the scroller's own coordinates (it's their
    // offsetParent, see position: relative below), the same ones scrollLeft
    // counts in, so the two are directly comparable.
    const focusPoint = scroller.scrollLeft + scroller.clientWidth / 2;
    const centers = slides.map((slide) => slide.offsetLeft + slideWidth / 2);

    centers.forEach((center, i) => {
      // The inner wrapper, never the slide itself: a slide IS the scroll-snap
      // area, and the spec defines that area as the *transformed* border box
      // -- scaling it would move the very point the browser is trying to snap
      // to, right as it snaps there. Scaling one level in leaves the snap
      // geometry perfectly still.
      const scaler = slides[i].firstElementChild as HTMLElement | null;
      if (!scaler) return;
      const offset = center - focusPoint;
      const distance = Math.min(1, Math.abs(offset) / slideWidth);
      // Shrunk towards the edge that's showing, not towards its own middle:
      // only CAROUSEL_PADDING of a neighbour is in view, and scaling about
      // its centre would pull that sliver clean off the screen -- the effect
      // would then only ever appear mid-swipe. Anchoring the visible edge
      // keeps the sliver exactly where it is and lets the card shrink away
      // from it, which is the cue that reads as "this one's further back".
      // Which edge stops mattering as a card reaches the middle, since by
      // then it's back at full size anyway.
      scaler.style.transformOrigin = offset >= 0 ? 'left center' : 'right center';
      scaler.style.transform = `scale(${FOCUSED_SCALE - (FOCUSED_SCALE - NEIGHBOUR_SCALE) * distance})`;
    });
    setIndex(Math.round(scroller.scrollLeft / slideWidth));
  }, []);

  // Sized straight on the DOM node rather than through React state: this runs
  // on every scroll frame, and re-rendering a card (photos and all) that often
  // just to change one transform drops frames on a phone. A transform doesn't
  // affect layout either, so none of the scroll/snap geometry above shifts
  // underneath it.
  //
  // Run on mount (and whenever the card set changes) too, or every card would
  // sit at full size until the first scroll -- the off-centre ones included.
  useLayoutEffect(syncToScroll, [syncToScroll, children.length]);

  return (
    <div>
      {/* scrollbar-width covers Firefox; this covers WebKit/Blink, which has
          no style-level equivalent. */}
      <style>{`.swipe-carousel::-webkit-scrollbar { display: none; }`}</style>
      <div
        ref={scrollerRef}
        className="swipe-carousel"
        onScroll={syncToScroll}
        style={{
          display: 'flex',
          // Makes this the slides' offsetParent, so their offsetLeft is
          // measured in the same coordinates as scrollLeft (see syncToScroll).
          position: 'relative',
          overflowX: 'auto',
          padding: CAROUSEL_PADDING,
          margin: `0 -${CAROUSEL_PADDING}px`,
          scrollSnapType: 'x mandatory',
          scrollbarWidth: 'none',
          WebkitOverflowScrolling: 'touch',
        }}
      >
        {children.map((child, i) => (
          <div
            key={i}
            style={{
              flex: '0 0 100%',
              minWidth: 0,
              scrollSnapAlign: 'center',
              // Without this a single flick can fly past several cards at
              // once -- on a group of a dozen that makes it impossible to
              // land on the one you actually saw.
              scrollSnapStop: 'always',
            }}
          >
            {/* The element syncToScroll scales -- see its own comment for why
                this sits inside the snap area rather than being it. */}
            <div>{child}</div>
          </div>
        ))}
      </div>
      {children.length > 1 && (
        <div style={{ display: 'flex', justifyContent: 'center', gap: 6, paddingBottom: 6 }}>
          {children.map((_, i) => (
            <span
              key={i}
              style={{
                width: 6,
                height: 6,
                borderRadius: '50%',
                // CORAL for the active one deliberately echoes the cluster
                // badge this carousel opens from.
                background: i === index ? CORAL : 'rgba(255,255,255,0.92)',
                boxShadow: '0 1px 3px rgba(0,0,0,0.32)',
                transition: 'background 160ms ease',
              }}
            />
          ))}
        </div>
      )}
    </div>
  );
};

export default SwipeCardCarousel;
